import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { newsAPI } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorBoundary from './ErrorBoundary';
import './ContentEditor.css';

function ContentEditor() {
  const navigate = useNavigate();
  const { contentId } = useParams();
  const isEditing = Boolean(contentId);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    contentType: 'news',
    title: '',
    content: '',
    status: 'draft',
    metaDescription: '',
    keywords: '',
    socialTitle: '',
    socialDescription: '',
    publishDate: '',
    categories: [],
    tags: []
  });
  
  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  
  // Categories and tags
  const [availableCategories] = useState([
    'News', 'Events', 'Safety', 'Weather', 'Training', 'Competition'
  ]);
  
  const [availableTags] = useState([
    'flying', 'weather', 'safety', 'training', 'competition', 'events', 
    'sites', 'equipment', 'beginners', 'advanced'
  ]);

  const loadContent = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // contentId is the news article id (no '#', so no URL-fragment issues).
      const article = await newsAPI.getById(contentId);
      if (article) {
        setFormData({
          contentType: 'news',
          title: article.title || '',
          // Migrated Joomla articles store the body in `introtext`; newer
          // articles use `content`. Prefer content, fall back to introtext so
          // legacy articles show their body (and get normalised to `content`
          // on the next save).
          content: article.content || article.introtext || '',
          status: (article.status || 'DRAFT').toLowerCase(),
          metaDescription: article.metaDescription || '',
          keywords: article.keywords || '',
          socialTitle: article.socialTitle || article.title || '',
          socialDescription: article.socialDescription || '',
          publishDate: article.publishDate || '',
          categories: article.categories || [],
          tags: article.tags || []
        });
      } else {
        throw new Error('News article not found');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading content:', err);
    } finally {
      setLoading(false);
    }
  }, [contentId]);

  useEffect(() => {
    if (!isEditing) return;
    let ignore = false;
    ;(async () => {
      try {
        await loadContent();
      } catch (err) {
        if (!ignore) console.error('Error loading content:', err);
      }
    })();
    return () => { ignore = true; };
  }, [isEditing, loadContent]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const handleArrayChange = (field, value, action = 'toggle') => {
    setFormData(prev => {
      const currentArray = prev[field] || [];
      let newArray;
      
      if (action === 'toggle') {
        newArray = currentArray.includes(value)
          ? currentArray.filter(item => item !== value)
          : [...currentArray, value];
      } else if (action === 'add' && !currentArray.includes(value)) {
        newArray = [...currentArray, value];
      } else if (action === 'remove') {
        newArray = currentArray.filter(item => item !== value);
      } else {
        newArray = currentArray;
      }
      
      return {
        ...prev,
        [field]: newArray
      };
    });
  };

  const generatePreview = () => {
    const preview = `
      <article class="content-preview">
        <header>
          <h1>${formData.title}</h1>
          <div class="meta">
            <span class="category">${formData.contentType}</span>
            <span class="status">${formData.status}</span>
            ${formData.publishDate ? `<span class="date">${new Date(formData.publishDate).toLocaleDateString()}</span>` : ''}
          </div>
        </header>
        <div class="content-body">
          ${formData.content.split('\n').map(line => `<p>${line}</p>`).join('')}
        </div>
        ${formData.tags.length > 0 ? `
          <footer class="tags">
            ${formData.tags.map(tag => `<span class="tag">${tag}</span>`).join('')}
          </footer>
        ` : ''}
      </article>
    `;
    setPreviewContent(preview);
    setShowPreview(true);
  };

  const handleSave = async (saveStatus = formData.status) => {
    // Validation
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (!formData.content.trim()) {
      setError('Content is required');
      return;
    }
    
    if (formData.title.length > 200) {
      setError('Title must be less than 200 characters');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const status = saveStatus === 'published' ? 'PUBLISHED' : 'DRAFT';
      const fields = {
        title: formData.title,
        content: formData.content,
        status,
      };

      // Edit existing article by id, or create a new one.
      const response = isEditing
        ? await newsAPI.update(contentId, fields)
        : await newsAPI.create(fields);

      if (response.success) {
        setSuccess(`News article ${status === 'PUBLISHED' ? 'published' : 'saved as draft'} successfully!`);
        // After creating, or after publishing, return to the admin list.
        if (!isEditing || status === 'PUBLISHED') {
          setTimeout(() => {
            navigate('/admin');
          }, 1500);
        }
      } else {
        throw new Error(response.message || 'Failed to save news article');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error saving content:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = () => handleSave('published');
  const handleSaveDraft = () => handleSave('draft');

  if (loading) {
    return (
      <div className="content-editor">
        <LoadingSpinner message="Loading content..." />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="content-editor">
        <header className="editor-header">
          <div className="header-left">
            <button 
              onClick={() => navigate('/admin')} 
              className="btn-back"
              disabled={saving}
            >
              ← Back to Admin
            </button>
            <h1>{isEditing ? 'Edit Content' : 'Create New Content'}</h1>
          </div>
          
          <div className="header-actions">
            <button 
              onClick={generatePreview}
              className="btn-secondary"
              disabled={saving}
            >
              Preview
            </button>
            <button 
              onClick={handleSaveDraft}
              className="btn-secondary"
              disabled={saving || !formData.title.trim() || !formData.content.trim()}
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button 
              onClick={handlePublish}
              className="btn-primary"
              disabled={saving || !formData.title.trim() || !formData.content.trim()}
            >
              {saving ? 'Publishing...' : 'Publish'}
            </button>
          </div>
        </header>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span className="alert-message">{error}</span>
            <button className="alert-close" onClick={() => setError(null)}>×</button>
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <span className="alert-icon">✅</span>
            <span className="alert-message">{success}</span>
            <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
          </div>
        )}

        <div className="editor-layout">
          <main className="editor-main">
            <div className="editor-form">
              {/* Basic Information */}
              <section className="form-section">
                <h2>Basic Information</h2>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="contentType">Content Type</label>
                    <select
                      id="contentType"
                      value={formData.contentType}
                      onChange={(e) => handleInputChange('contentType', e.target.value)}
                      disabled={saving || isEditing}
                    >
                      <option value="news">News</option>
                      <option value="about">About</option>
                      <option value="club_info">Club Info</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      disabled={saving}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="title">Title</label>
                  <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter content title"
                    disabled={saving}
                    maxLength="200"
                    required
                  />
                  <small className="form-help">{formData.title.length}/200 characters</small>
                </div>

                <div className="form-group">
                  <label htmlFor="publishDate">Publish Date (optional)</label>
                  <input
                    type="datetime-local"
                    id="publishDate"
                    value={formData.publishDate}
                    onChange={(e) => handleInputChange('publishDate', e.target.value)}
                    disabled={saving}
                  />
                  <small className="form-help">Leave empty to publish immediately</small>
                </div>
              </section>

              {/* Content */}
              <section className="form-section">
                <h2>Content</h2>
                
                <div className="form-group">
                  <label htmlFor="content">Content Body</label>
                  <textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => handleInputChange('content', e.target.value)}
                    placeholder="Enter your content here..."
                    rows="15"
                    disabled={saving}
                    required
                  />
                  <small className="form-help">
                    Use line breaks to separate paragraphs. Basic formatting will be applied automatically.
                  </small>
                </div>
              </section>

              {/* Categories and Tags */}
              <section className="form-section">
                <h2>Organization</h2>
                
                <div className="form-group">
                  <label>Categories</label>
                  <div className="checkbox-group">
                    {availableCategories.map(category => (
                      <label key={category} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.categories.includes(category)}
                          onChange={() => handleArrayChange('categories', category)}
                          disabled={saving}
                        />
                        {category}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Tags</label>
                  <div className="tag-selector">
                    {availableTags.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        className={`tag-button ${formData.tags.includes(tag) ? 'selected' : ''}`}
                        onClick={() => handleArrayChange('tags', tag)}
                        disabled={saving}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="selected-tags">
                      <small>Selected: {formData.tags.join(', ')}</small>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </main>

          <aside className="editor-sidebar">
            {/* SEO Section */}
            <section className="sidebar-section">
              <h3>SEO Optimization</h3>
              
              <div className="form-group">
                <label htmlFor="metaDescription">Meta Description</label>
                <textarea
                  id="metaDescription"
                  value={formData.metaDescription}
                  onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                  placeholder="Brief description for search engines"
                  rows="3"
                  maxLength="160"
                  disabled={saving}
                />
                <small className="form-help">{formData.metaDescription.length}/160 characters</small>
              </div>

              <div className="form-group">
                <label htmlFor="keywords">Keywords</label>
                <input
                  type="text"
                  id="keywords"
                  value={formData.keywords}
                  onChange={(e) => handleInputChange('keywords', e.target.value)}
                  placeholder="keyword1, keyword2, keyword3"
                  disabled={saving}
                />
                <small className="form-help">Comma-separated keywords</small>
              </div>
            </section>

            {/* Social Media Section */}
            <section className="sidebar-section">
              <h3>Social Media</h3>
              
              <div className="form-group">
                <label htmlFor="socialTitle">Social Media Title</label>
                <input
                  type="text"
                  id="socialTitle"
                  value={formData.socialTitle}
                  onChange={(e) => handleInputChange('socialTitle', e.target.value)}
                  placeholder="Title for social media sharing"
                  maxLength="60"
                  disabled={saving}
                />
                <small className="form-help">{formData.socialTitle.length}/60 characters</small>
              </div>

              <div className="form-group">
                <label htmlFor="socialDescription">Social Media Description</label>
                <textarea
                  id="socialDescription"
                  value={formData.socialDescription}
                  onChange={(e) => handleInputChange('socialDescription', e.target.value)}
                  placeholder="Description for social media sharing"
                  rows="3"
                  maxLength="120"
                  disabled={saving}
                />
                <small className="form-help">{formData.socialDescription.length}/120 characters</small>
              </div>
            </section>

            {/* Content Stats */}
            <section className="sidebar-section">
              <h3>Content Statistics</h3>
              <div className="content-stats">
                <div className="stat">
                  <span className="stat-label">Words:</span>
                  <span className="stat-value">
                    {formData.content.trim().split(/\s+/).filter(word => word.length > 0).length}
                  </span>
                </div>
                <div className="stat">
                  <span className="stat-label">Characters:</span>
                  <span className="stat-value">{formData.content.length}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Paragraphs:</span>
                  <span className="stat-value">
                    {formData.content.trim().split('\n').filter(line => line.trim().length > 0).length}
                  </span>
                </div>
              </div>
            </section>
          </aside>
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <div className="preview-modal">
            <div className="preview-modal-content">
              <header className="preview-header">
                <h2>Content Preview</h2>
                <button 
                  onClick={() => setShowPreview(false)}
                  className="btn-close"
                >
                  ×
                </button>
              </header>
              <div 
                className="preview-body"
                dangerouslySetInnerHTML={{ __html: previewContent }}
              />
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default ContentEditor;